import { Component, OnInit, Input, Output, EventEmitter, OnDestroy } from '@angular/core';
import { Subscription } from 'rxjs';
import { ApplicationSectionUtils } from '../../../../classes/ApplicationSection';
import { NavigationService } from '../../../../services/navigation/navigation.service';
import { ApplicationService } from '../../../../services/application/application.service';
import {
  TotalAnsweredQuestionsForProgressPercentageInterface,
  TotalQuestionsForProgressPercentageInterface,
} from '../../../../interfaces/Type';

@Component({
  moduleId: module.id,
  selector: 'section-row',
  templateUrl: './section-row.component.html',
})
export class SectionRowComponent implements OnInit, OnDestroy {
  @Input() sectionName: string;
  @Input() shouldHideProgress: boolean = false;
  @Input() isDisabled: boolean = false;
  @Input() isError: boolean = false;
  @Output() sectionRowClicked: EventEmitter<any> = new EventEmitter();
  isActiveSection: boolean;
  @Output() numberOfTotalQuestionsEmitter: EventEmitter<TotalQuestionsForProgressPercentageInterface> =
    new EventEmitter();
  @Output() numberOfAnsweredQuestionsEmitter: EventEmitter<TotalAnsweredQuestionsForProgressPercentageInterface> =
    new EventEmitter();
  private numberOfTotalQuestions: number = 0;
  private numberOfAnsweredQuestions: number = 0;
  private afterSectionLoadedSubscription: Subscription;
  private questionAnsweredSubscription: Subscription;
  private sectionValidationSubscription: Subscription;
  isCompleted: boolean = false;
  progressPercentage: number;
  cpFilingType: string;

  constructor(public appService: ApplicationService, private navigationService: NavigationService) {}

  isAdvisor: boolean = false;
  sectionEnabled: boolean = false;
  answeredProgress: string;
  questionsAnsweredSub: Subscription;

  ngOnInit() {
    this.cpFilingType = this.appService.appState.controller.customer.getCoverpathFilingType();
    this.updateProgressPercent();

    this.afterSectionLoadedSubscription = this.navigationService.afterActiveSectionLoaded$.subscribe(
      (activeSection) => {
        const wasActive = this.isActiveSection;
        this.isActiveSection =
          ApplicationSectionUtils.getAllSections(this.cpFilingType, this.appService.appState)[activeSection] ===
          this.sectionName
            ? true
            : false;
        if (wasActive && !this.isActiveSection) {
          this.updateProgressPercent();
        }
      }
    );
    this.questionAnsweredSubscription = this.appService.questionAnsweredObservable$.subscribe(() => {
      this.updateProgressPercent();
    });
    this.sectionValidationSubscription = this.appService.appState.sectionValidationObservable$.subscribe(() => {
      this.updateProgressPercent();
    });
    this.isAdvisor = this.appService.appState.isAdvisor;
    this.answeredProgress = this.getAnsweredProgress();
    this.updateSectionEnabled();
    this.questionsAnsweredSub = this.appService.questionAnsweredObservable$.subscribe(() => {
      this.answeredProgress = this.getAnsweredProgress();
      this.updateSectionEnabled();
    });
  }

  ngOnDestroy() {
    if (this.afterSectionLoadedSubscription) {
      this.afterSectionLoadedSubscription.unsubscribe();
    }
    if (this.questionAnsweredSubscription) {
      this.questionAnsweredSubscription.unsubscribe();
    }
    if (this.sectionValidationSubscription) {
      this.sectionValidationSubscription.unsubscribe();
    }
    if (this.questionsAnsweredSub) {
      this.questionsAnsweredSub.unsubscribe();
    }
  }

  getSectionNumber(): number {
    return ApplicationSectionUtils.getSectionNumberByName(
      this.sectionName,
      this.cpFilingType,
      this.appService.appState
    );
  }

  emitEvent() {
    if (!this.isDisabled) {
      this.sectionRowClicked.emit(this.getSectionNumber());
    }
  }

  getCurrentPercentage(percentage: number): number {
    const perc = Math.round(percentage * 100);
    if (isNaN(perc)) {
      return 0;
    } else {
      return perc;
    }
  }

  setTotalShownQuestionsInSection() {
    this.numberOfTotalQuestions = this.appService.getShownSectionQuestions(this.getSectionNumber()).length;
    const emitValue: TotalQuestionsForProgressPercentageInterface = {
      sectionName: this.sectionName,
      numberOfTotalQuestions: this.numberOfTotalQuestions,
    };
    this.numberOfTotalQuestionsEmitter.emit(emitValue);
  }

  setTotalAnsweredQuestionsInSection() {
    this.numberOfAnsweredQuestions = this.appService.getShownAnsweredSectionQuestions(this.getSectionNumber()).length;
    const emitValue: TotalAnsweredQuestionsForProgressPercentageInterface = {
      sectionName: this.sectionName,
      numberOfAnsweredQuestions: this.numberOfAnsweredQuestions,
    };
    this.numberOfAnsweredQuestionsEmitter.emit(emitValue);
  }

  updateProgressPercent(): void {
    this.appService.appState.percentComplete[this.getSectionNumber()] = this.getCurrentPercentage(
      this.calculateProgressPercent()
    );
    this.progressPercentage = this.appService.appState.percentComplete[this.getSectionNumber()];
    this.isCompleted = this.progressPercentage >= 100;
  }

  calculateProgressPercent(): number {
    this.setTotalShownQuestionsInSection();
    this.setTotalAnsweredQuestionsInSection();
    let percentFilled = 0;

    if (this.appService.appState.invalidSection[this.sectionName] !== undefined) {
      const numberOfAnsweredQuestionsWithNoErrors =
        this.numberOfAnsweredQuestions - this.appService.appState.invalidSection[this.sectionName].count;
      percentFilled = Math.round((numberOfAnsweredQuestionsWithNoErrors / this.numberOfTotalQuestions) * 100) / 100;
    } else if (this.numberOfAnsweredQuestions === 0 && this.numberOfTotalQuestions === 0) {
      percentFilled = 1;
    } else {
      percentFilled = Math.round((this.numberOfAnsweredQuestions / this.numberOfTotalQuestions) * 100) / 100;
    }
    return percentFilled > 1 ? 1 : percentFilled;
  }

  protected getAnsweredProgress(): string {
    return `${this.numberOfAnsweredQuestions} / ${this.numberOfTotalQuestions}`;
  }

  // the section is enabled if all the previous sections are complete or if the advisor is logged in
  updateSectionEnabled(): void {
    this.sectionEnabled = this.appService.appState.previousSectionsComplete(this.getSectionNumber()) || this.isAdvisor;
  }
}
